<script setup>
import { Link } from '@inertiajs/vue3'
import { getFormColor } from '../../../composables/useReservationsCalendar.js'
import { trans } from 'laravel-vue-i18n'

const props = defineProps({
    results: { type: Array, default: () => [] },
    query:   { type: String, default: '' },
})

const STATUS_LABELS = {
    accepted: trans('reservations.state_accepted_label'),
    pending: trans('reservations.state_pending_label'),
    rejected: trans('reservations.state_rejected_label'),
}

function timeRange(item) {
    if (item.time_start) {
        return `${item.time_start.substring(0, 5)}${item.time_end ? ' – ' + item.time_end.substring(0, 5) : ''}`
    }
    if (item.formatted_date_end && item.formatted_date_end !== item.formatted_date) {
        return `${item.formatted_date} – ${item.formatted_date_end}`
    }
    return item.formatted_date ?? ''
}
</script>

<template>
    <div class="h-full flex flex-col overflow-hidden">
        <!-- Header -->
        <div class="flex-shrink-0 px-4 py-2.5 border-b border-neutral-100 dark:border-neutral-800">
            <span class="text-xs text-neutral-400 dark:text-neutral-500">
                {{ results.length }} {{ $t('reservations.cal_res_suffix') }}
            </span>
        </div>

        <!-- Empty state -->
        <div
            v-if="!results.length"
            class="flex-1 flex flex-col items-center justify-center gap-3 text-neutral-400 dark:text-neutral-500"
        >
            <i class="pi pi-search text-3xl opacity-40"/>
            <p class="text-sm">{{ $t('reservations.search_no_results') }} <span class="font-medium text-neutral-600 dark:text-neutral-300">„{{ query }}"</span></p>
        </div>

        <!-- Results list -->
        <div v-else class="flex-1 overflow-y-auto divide-y divide-neutral-100 dark:divide-neutral-800">
            <Link
                v-for="item in results"
                :key="item.id"
                :href="`/app/reservations/${item.sqid}`"
                class="emify flex items-start gap-3 px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
            >
                <!-- Form color dot -->
                <div
                    class="w-2 h-2 rounded-sm flex-shrink-0 mt-1.5"
                    :style="{ background: getFormColor(item.reservation_forms) }"
                />

                <!-- Main content -->
                <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2">
                        <span
                            class="text-sm font-medium text-neutral-800 dark:text-neutral-100 truncate"
                            v-html="item._highlighted['name'] ?? item.name"
                        />
                        <span
                            class="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                            :class="{
                                'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400': item.state === 'accepted',
                                'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400':   item.state === 'pending',
                                'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400':            item.state === 'rejected',
                            }"
                        >
                            {{ STATUS_LABELS[item.state] ?? item.state }}
                        </span>
                    </div>

                    <div
                        class="text-xs text-neutral-400 dark:text-neutral-500 truncate mt-0.5"
                        v-html="item._highlighted['email'] ?? item.email"
                    />

                    <div class="flex items-center gap-1.5 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        <span
                            class="font-medium truncate"
                            v-html="item._highlighted['reservation_forms.name'] ?? item.reservation_forms?.name"
                        />
                        <span class="text-neutral-300 dark:text-neutral-600">·</span>
                        <span class="flex-shrink-0">{{ timeRange(item) }}</span>
                    </div>
                </div>
            </Link>
        </div>
    </div>
</template>
